import * as React from 'react';
import { useMemo } from 'react';
import { css } from '@emotion/core';
import Card from '../../layout/card/Card';
import CardHeader from '../../layout/card/CardHeader';
import { Str } from '../../localization/translationKeys';
import { useMsgText } from '../../localization/hooks';
import { FLEX_COLUMN } from '../../layout/flexBox';
import { btnStyle } from '../../forms/styles/button';
import { COLORS } from '../../globalStyle/colors';
import { Link } from 'react-router-dom';
import Msg from '../../localization/Msg';
import { ellipsizeAt } from '../../layout/styles';
import { DevPlanItem } from './models';
import LocalDiv from '../../localization/LocalDiv';


const styles = {
  root:                 [
    FLEX_COLUMN,
    css`
      padding: 0;
      overflow: hidden;
      align-items: stretch;
    `,
  ],
  header:               css`
    padding-top: 14px;
    margin-left: 14px;
    margin-right: 14px;
    margin-bottom: 40px;
   
    * { 
      font-size: 12px !important;
      color: ${COLORS.MEDIUM_GREY} !important;
    } 
  `,
  itemTitle:            css`
    font-size: 14px;
    font-weight: bold;
    margin-left: 28px;
    margin-right: 28px;
    color: ${COLORS.DARK_GREY};
  `,
  itemDesc:             [
    css`
      height: 85px;
      padding: 0 10px 10px; /* Inexplicably, not having padding breaks ellipsizing */
      margin: 0 18px 30px;
      
      font-size: 12px;
      line-height: 16px;
      color: ${COLORS.DARK_GREY};
    `,
    ellipsizeAt(6),
  ],
  itemDescPlaceholder:  css`
    color: ${COLORS.FIELD_GREY};
  `,
  footer:               [
    FLEX_COLUMN,
    css`
      align-items: stretch;
    `,
  ],
  actions:              [
    css`
      font-size: 12px;
      text-align: center;
      padding: 10px;
    `,
  ],
  link:                 css`
    text-decoration: none;
  `,
  hasUnreadCommentsBtn: [
    btnStyle.primary(COLORS.RED),
    css`
      border-radius: 0;
    `,
  ],
  noUnreadCommentsBtn:  [
    btnStyle.primary(COLORS.UNREAD_GREY),
    css`
      border-radius: 0;
    `,
  ],
  viewDetailsBtn:       [
    btnStyle.primary(COLORS.DARK_GREY),
    css`
      border-radius: 0;
    `,
  ],
};


type Props = {
  item: DevPlanItem;
  itemLocation: string;
  actionCount: number;

  collaborationEnabled: boolean;
  isParticipant: boolean;
  unreadCommentCount: number;

  className?: string;
};


/**
 * DevItemCard: a component that renders a summary of a dev plan item (title/desc + action count and a link to view)
 *
 * PureComponent: true
 * Redux Connected: false
 */
const DevItemCard: React.FC<Props> = props => {
  const title           = useMsgText(Str.Devitem);
  const descPlaceholder = useMsgText(Str.DescriptionNotAdded);
  const unreadDefault   = useMsgText(Str.UnreadCommentsMsg);
  const viewDefault     = useMsgText(props.isParticipant ? Str.ViewEditDetails : Str.ViewDetails)

  const actionColors = useMemo(
    () => css`
      background-color: ${props.actionCount > 0 ? COLORS.LIGHTEST_GREY : COLORS.GREEN};
      color: ${props.actionCount > 0 ? COLORS.DARK_GREY : COLORS.WHITE};
    `,
    [props.actionCount],
  );

  return (
    <Card css={styles.root} className={props.className}>
      <CardHeader title={title} css={styles.header} />

      <span css={styles.itemTitle}>{props.item.title}</span>
      <p css={[styles.itemDesc, !props.item.description && styles.itemDescPlaceholder]}>
        {props.item.description || descPlaceholder}
      </p>

      <div css={styles.footer}>
        <Msg str={Str.ActionsAdded} replace={{ number: props.actionCount }} css={[styles.actions, actionColors]} />
        <Link to={props.itemLocation} css={styles.link}>
          {
            // TODO - the current design of the database only allows tracking read status for the participant
            // This will later be re-implemented to track unread comments for collaborators as well
            props.collaborationEnabled && (
              <LocalDiv
                css={props.unreadCommentCount > 0 ? styles.hasUnreadCommentsBtn : styles.noUnreadCommentsBtn}
              >
                <Msg
                  str={Str.UnreadCommentsMsg}
                  replace={{ 'number': props.unreadCommentCount }}
                  defaultMsg={unreadDefault}
                  />
              </LocalDiv>
            )
          }
          <LocalDiv css={styles.viewDetailsBtn}>
            <Msg 
              str={props.isParticipant ? Str.ViewEditDetails : Str.ViewDetails}
              defaultMsg={viewDefault}
            />
          </LocalDiv>
        </Link>
      </div>
    </Card>
  );
};

export default DevItemCard;
